import { useParams } from 'react-router-dom';
import { useCancelBooking } from '../hooks/use-public-booking';
import { DEFAULT_SLUG } from './BookPage';

/**
 * Cancel a booking from the link in the confirmation email.
 *
 * The token in the URL is a CAPABILITY, not an identifier. Anybody holding the link
 * can cancel, which is fine, because cancelling is all it can do. What follows from
 * that is the rule this page exists to enforce: NOTHING about the booking is rendered
 * before the POST. No name, no time, no company. A link forwarded to the wrong person
 * or scraped from a mailbox must not become a way to read who is meeting whom.
 */

const NAVY = '#0E2841';
const SKY = '#43ABDB';
const DEEP = '#041A2B';
const INK = '#404040';
const MUTED = '#6B7A85';
const CORAL = '#FF5151';

const REBOOK_HREF = `/book/${DEFAULT_SLUG}/`;

const rebookStyle = {
  display: 'block', minHeight: 48, lineHeight: '48px', textAlign: 'center' as const,
  background: SKY, color: DEEP, borderRadius: 8, fontWeight: 600, textDecoration: 'none',
};

export default function BookCancelPage() {
  const { token } = useParams<{ token: string }>();
  const cancel = useCancelBooking(token);

  const notFound = cancel.isError && cancel.error?.status === 404;

  return (
    <div style={{ color: INK, fontFamily: 'Manrope, system-ui, sans-serif' }}>
      {cancel.isSuccess ? (
        <>
          <h1 style={{ margin: '0 0 10px', fontSize: 21, fontWeight: 600, color: NAVY }}>
            {cancel.data!.already_cancelled ? 'That one was already cancelled' : 'Cancelled'}
          </h1>
          <p style={{ margin: '0 0 20px', fontSize: 15, lineHeight: 1.6, color: MUTED }}>
            {cancel.data!.already_cancelled
              ? 'Nothing to do here. The time is free and nobody is expecting you.'
              : 'The time is free again and Todd has been told. No worries at all.'}
          </p>
          <a href={REBOOK_HREF} style={rebookStyle}>Book another time</a>
        </>
      ) : notFound ? (
        <>
          <h1 style={{ margin: '0 0 10px', fontSize: 21, fontWeight: 600, color: NAVY }}>
            We can't find that booking
          </h1>
          <p style={{ margin: '0 0 20px', fontSize: 15, lineHeight: 1.6, color: MUTED }}>
            It may already have been cancelled, or the link may have been cut short by an email client.
          </p>
          <a href={REBOOK_HREF} style={rebookStyle}>Book a time</a>
        </>
      ) : (
        <>
          <h1 style={{ margin: '0 0 10px', fontSize: 21, fontWeight: 600, color: NAVY }}>
            Cancel this booking?
          </h1>
          <p style={{ margin: '0 0 20px', fontSize: 15, lineHeight: 1.6, color: MUTED }}>
            The time goes back on the calendar and Todd is told. You can always book another one.
          </p>

          {cancel.isError && !notFound ? (
            <p role="alert" style={{ margin: '0 0 16px', fontSize: 15, color: CORAL }}>
              That didn't go through. Give it a moment and try again.
            </p>
          ) : null}

          <button
            type="button"
            onClick={() => cancel.mutate()}
            disabled={cancel.isPending}
            style={{
              width: '100%', minHeight: 48, border: 'none', borderRadius: 8,
              background: cancel.isPending ? '#B3BDC2' : SKY, color: DEEP,
              fontSize: 16, fontWeight: 600, fontFamily: 'inherit',
              cursor: cancel.isPending ? 'not-allowed' : 'pointer',
            }}
          >
            {cancel.isPending ? 'Cancelling…' : 'Yes, cancel it'}
          </button>
        </>
      )}
    </div>
  );
}
